import { ReactNode } from "react";
import { ErrorBoundary as RollbarErrorBoundary } from "@rollbar/react";
import { Button } from "@hipotecar/ui/button";

interface ErrorBoundaryProps {
	children: ReactNode;
}

interface FallbackUIProps {
	error: Error | null;
	resetError: () => void;
}

function FallbackUI({ error, resetError }: FallbackUIProps) {
	if (!error) return null;
	return (
		<div className="flex min-h-screen items-center justify-center bg-surface px-4">
			<div className="w-full max-w-md">
				<div className="rounded-lg border border-red-200 bg-card p-8 shadow-lg dark:border-red-900">
					{/* Error Icon */}
					<div className="mb-4 flex justify-center">
						<div className="rounded-full bg-red-100 p-3 dark:bg-red-900/20">
							<svg
								className="size-8 text-red-600 dark:text-red-500"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
								/>
							</svg>
						</div>
					</div>

					{/* Error Message */}
					<h2 className="heading-lg mb-2 text-center text-2xl font-bold">
						Oops! Something went wrong
					</h2>
					<p className="mb-6 text-center text-secondary">
						We've been notified and are working to fix the issue. Please try
						again.
					</p>

					{/* Error Details (only in development) */}
					{import.meta.env.DEV && (
						<div className="mb-6 rounded-sm bg-elevated p-4">
							<p className="mb-2 text-sm font-semibold text-primary">
								Error Details:
							</p>
							<p className="font-mono text-xs wrap-break-word text-red-600 dark:text-red-400">
								{error.message}
							</p>
						</div>
					)}

					{/* Action Buttons */}
					<div className="flex flex-col gap-3 sm:flex-row">
						<Button
							variant="primary"
							size="md"
							onClick={resetError}
							className="flex-1"
						>
							Try Again
						</Button>
						<Button
							variant="outline"
							size="md"
							onClick={() => (window.location.href = "/")}
							className="flex-1"
						>
							Go Home
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
}

export function ErrorBoundary({ children }: ErrorBoundaryProps) {
	return (
		<RollbarErrorBoundary fallbackUI={FallbackUI}>
			{children}
		</RollbarErrorBoundary>
	);
}
